/* ============================================================
   IA Landing PDP — product-switching buy block.
   Options select different PRODUCTS. Switching swaps price,
   description, gallery and the active Skio plan picker WITHOUT
   reloading.

   Gallery reuses the theme's detail slider (od-product-media /
   <od-product-slider>), rendered once per product into slots;
   on switch we show the active slot (with a fade) and nudge its
   Swiper to recalc (it may have initialised while hidden).

   Subscribe/one-time is the real Skio widget, one per product;
   only the active slot's selling_plan input stays enabled.
   Price + URL sync from Skio's `skio::update-selling-plan` event.
   ============================================================ */
if (!customElements.get("ia-lndg-pdp")) {
  customElements.define(
    "ia-lndg-pdp",
    class IaLndgPdp extends HTMLElement {
      connectedCallback() {
        this.data = this._readData();
        this.current = 0;

        this.options = Array.from(this.querySelectorAll("[data-pdp-option]"));
        this.idInput = this.querySelector("[data-pdp-id]");
        this.priceEl = this.querySelector("[data-pdp-price]");
        this.valueEl = this.querySelector("[data-pdp-value]");
        this.stickyPriceEl = this.querySelector("[data-pdp-sticky-price]");
        this.stickyValueEl = this.querySelector("[data-pdp-sticky-value]");
        this.afterpayEl = this.querySelector("[data-pdp-afterpay]");
        this.pointsSlots = Array.from(this.querySelectorAll("[data-pdp-points-slot]"));
        this.skioSlots = Array.from(this.querySelectorAll("[data-pdp-skio-slot]"));
        this.galleryLive = this.querySelector("[data-pdp-gallery-live]");
        this.scrollBtn = this.querySelector("[data-pdp-scroll]");
        this.scrollTargetEl = this.querySelector(".ia-lndg-pdp__title");
        this.ratingEl = this.querySelector("[data-pdp-rating]");
        this.basePrice = (this.data[0] && this.data[0].price) || "";

        this.options.forEach((btn) =>
          btn.addEventListener("click", () => this.select(Number(btn.dataset.index)))
        );

        // Sticky CTA: scroll back to this block instead of adding to cart.
        if (this.scrollBtn)
          this.scrollBtn.addEventListener("click", () => this._scrollToBuy());

        // Rating: scroll down to the Junip reviews widget. Capture phase so it
        // fires even if Junip's own summary click handler stops propagation.
        if (this.ratingEl)
          this.ratingEl.addEventListener("click", () => this._scrollToReviews(), true);

        this._syncSkio(0);
        this.addEventListener("skio::update-selling-plan", (e) => this._onSkioPlan(e));
        this._watchGallery();
        this._styleSkioPickers();
      }

      /* Skio plan picker is a shadow-DOM Lit component — theme CSS can't reach its
         .group-title, and we must NOT edit the app file (Skio can overwrite it). Inject a
         scoped <style> into each picker's shadow root. `.group-topline .group-title` (0,2,0)
         outranks the component's `.group-title` (0,1,0), so no !important is needed. */
      _styleSkioPickers() {
        const css =
          ".group-topline .group-title{max-width:145px}" +
          "@media (min-width:1200px){.group-topline .group-title{max-width:53%}}";
        customElements.whenDefined("skio-plan-picker").then(() => {
          this.querySelectorAll("skio-plan-picker").forEach((el) => {
            (el.updateComplete || Promise.resolve()).then(() => {
              const root = el.shadowRoot;
              if (!root || root.querySelector("style[data-ia-skio]")) return;
              const s = document.createElement("style");
              s.setAttribute("data-ia-skio", "");
              s.textContent = css;
              root.appendChild(s);
            });
          });
        });
      }

      /* Smooth-scroll to this PDP block — the sticky CTA target. */
      _scrollToBuy() {
        var el = this.scrollTargetEl || this;
        try {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (e) {
          el.scrollIntoView();
        }
      }

      /* Clicking the rating scrolls down to the Junip reviews widget. */
      _scrollToReviews() {
        var sel =
          (this.ratingEl && this.ratingEl.dataset.pdpReviewsTarget) ||
          ".junip-product-review";
        var el = document.querySelector(sel);
        if (!el) return;
        try {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (e) {
          el.scrollIntoView();
        }
      }

      /* Keep the in-card price and the sticky CTA price in sync. */
      _setPrice(text) {
        if (this.priceEl) this.priceEl.textContent = text;
        if (this.stickyPriceEl) this.stickyPriceEl.textContent = text;
      }

      _readData() {
        const out = [];
        this.querySelectorAll("[data-pdp-data]").forEach((el) => {
          try {
            out[Number(el.dataset.pdpData)] = JSON.parse(el.textContent);
          } catch (e) {
            console.error("[ia-lndg-pdp] bad product data", e);
          }
        });
        return out;
      }

      /* ---------- product switching ----------
         opts.forceOneTime: land on the one-time (no-subscription) option even if
         this product has a subscription — used when the hero picker drives us. */
      select(index, opts) {
        const forceOneTime = !!(opts && opts.forceOneTime);
        if (!this.data[index]) return;
        if (index === this.current) {
          // Same product already shown: still honor a one-time request.
          if (forceOneTime) this._skioOneTime(index);
          return;
        }
        const d = this.data[index];

        this.options.forEach((b) => {
          const on = Number(b.dataset.index) === index;
          b.classList.toggle("is-selected", on);
          b.setAttribute("aria-checked", on ? "true" : "false");
        });

        if (this.idInput) this.idInput.value = d.variantId;
        // Afterpay <square-placement> recomputes the ÷4 from data-amount (full base price).
        if (this.afterpayEl && d.priceAmount)
          this.afterpayEl.setAttribute("data-amount", d.priceAmount.replace(/,/g, ""));
        this._syncPoints(index);

        this.basePrice = d.price || "";
        const subFirst = !forceOneTime && d.hasSub && d.plans && d.plans[0];
        this._setPrice(subFirst ? subFirst.price : d.price || "");
        if (this.valueEl) {
          this.valueEl.textContent = d.value || "";
          this.valueEl.hidden = !d.value;
        }
        if (this.stickyValueEl) {
          this.stickyValueEl.textContent = d.value || "";
          this.stickyValueEl.hidden = !d.value;
        }
        this._updateUrl(subFirst ? subFirst.id : null);

        this._syncSkio(index);
        this._syncGallery(index);
        this.current = index;
        // Flip the (now active) Skio picker to one-time after current is set, so
        // its skio::update-selling-plan event is processed for this product.
        if (forceOneTime) this._skioOneTime(index);
      }

      /* Force the active product's Skio picker onto "One time purchase".
         selectSellingPlanGroup(null) is the component's own method; it clears
         selectedSellingPlan → fires skio::update-selling-plan (handled by
         _onSkioPlan → one-time price) and updates the hidden selling_plan input. */
      _skioOneTime(index) {
        const slot = this.skioSlots.find(
          (s) => Number(s.dataset.pdpSkioSlot) === index
        );
        const d = this.data[index] || {};
        const apply = () => {
          try {
            const picker = slot && slot.querySelector("skio-plan-picker");
            if (picker && typeof picker.selectSellingPlanGroup === "function")
              picker.selectSellingPlanGroup(null);
          } catch (e) {}
          // Belt-and-suspenders: if the picker was already one-time it won't fire
          // an event, so make sure the card shows the one-time price.
          this._setPrice(d.price || this.basePrice);
          this._updateUrl(null);
        };
        if (window.customElements && customElements.whenDefined)
          customElements.whenDefined("skio-plan-picker").then(apply);
        else apply();
      }

      /* ---------- gallery: re-render the detail slider from <template> ---------- */
      _syncGallery(index) {
        const tpl = this.querySelector('[data-pdp-gallery-tpl="' + index + '"]');
        if (!tpl || !this.galleryLive) return;
        this.galleryLive.classList.add("is-enter"); // fade-in on product switch
        this.galleryLive.replaceChildren(tpl.content.cloneNode(true));
        requestAnimationFrame(() => {
          this.galleryLive.classList.remove("is-enter");
          // Re-init thumbnails THEN main so the main slider links to the thumbnails
          // swiper (the clone connects main before thumbnails) and both re-measure
          // against the now-laid-out gallery.
          this._refreshGallery();
          // Firefox sizes lazy images before they load → squished; re-measure once they do.
          setTimeout(() => this._updateGallery(), 250);
        });
      }

      /* Destroy + re-init both gallery sliders (thumbnails first so the main one can
         link to it). Fixes blank / 0-width Swiper inits (Firefox) + thumbnail linking. */
      _refreshGallery() {
        if (!this.galleryLive) return;
        [
          this.galleryLive.querySelector("base-slider"),
          this.galleryLive.querySelector("od-product-slider"),
        ].forEach((el) => {
          if (!el) return;
          el.dispatchEvent(new CustomEvent("slider:destroy"));
          el.dispatchEvent(new CustomEvent("slider:init"));
        });
      }

      /* Lightweight Swiper.update() on both gallery sliders (recomputes sizes). */
      _updateGallery() {
        if (!this.galleryLive) return;
        this.galleryLive
          .querySelectorAll("od-product-slider, base-slider")
          .forEach((el) => el.dispatchEvent(new CustomEvent("slider:update")));
      }

      /* Firefox: the reused Swiper gallery can measure before the flex/sticky width
         settles (→ blank) or before images load (→ squished). Re-init once layout is
         ready, then update on any later size change of the gallery box. */
      _watchGallery() {
        const refresh = () => this._refreshGallery();
        if (document.readyState === "complete") requestAnimationFrame(refresh);
        else
          window.addEventListener("load", () => requestAnimationFrame(refresh), {
            once: true,
          });
        if (window.ResizeObserver && this.galleryLive) {
          let t;
          this._galleryRO = new ResizeObserver(() => {
            clearTimeout(t);
            t = setTimeout(() => this._updateGallery(), 100);
          });
          this._galleryRO.observe(this.galleryLive);
        }
      }

      /* ---------- Skio slots ---------- */
      _syncSkio(index) {
        this.skioSlots.forEach((slot) => {
          const active = Number(slot.dataset.pdpSkioSlot) === index;
          slot.hidden = !active;
          slot.querySelectorAll('[name="selling_plan"]').forEach((inp) => {
            inp.disabled = !active;
          });
        });
      }

      _syncPoints(index) {
        this.pointsSlots.forEach((s) => {
          s.hidden = Number(s.dataset.pdpPointsSlot) !== index;
        });
      }

      _onSkioPlan(e) {
        const slot = e.target.closest("[data-pdp-skio-slot]");
        if (!slot || Number(slot.dataset.pdpSkioSlot) !== this.current) return;
        const plan = e.detail && e.detail.sellingPlan;
        const id = plan && plan.id;
        const d = this.data[this.current] || {};
        if (id && d.plans) {
          const found = d.plans.find((x) => String(x.id) === String(id));
          if (found) this._setPrice(found.price);
          this._updateUrl(id);
        } else {
          this._setPrice(d.price || this.basePrice);
          this._updateUrl(null);
        }
      }

      _updateUrl(planId) {
        try {
          const url = new URL(window.location.href);
          if (planId) url.searchParams.set("selling_plan", planId);
          else url.searchParams.delete("selling_plan");
          history.replaceState(history.state, "", url);
        } catch (e) {
          /* no-op */
        }
      }
    }
  );
}
